import { useCallback, useEffect, useRef, useState } from 'react';

import { App } from 'antd';
import { useTranslation } from 'react-i18next';

import { ApiErrorData } from 'models/ApiResponse';
import { TiendaPerfectaRouteReport } from 'models/TiendaPerfecta';

import { TiendaPerfectaService } from 'services/TiendaPerfectaService';

/** Cada cuánto se vuelve a pedir el día mientras la pantalla está a la vista. */
export const POLL_INTERVAL_MS = 30_000;

interface UseRouteReportParams {
  /** `AAAA-MM-DD`. Sin fecha no se consulta. */
  date: string | undefined;
  /** `undefined` es "Todos": se omite el parámetro y el servidor manda todo. */
  distributorId?: string;
}

interface UseRouteReportReturn {
  /** Solo la primera carga de una fecha: es la que muestra el skeleton. */
  loading: boolean;
  /** Refresco en segundo plano: no desmonta nada, solo marca actividad. */
  refreshing: boolean;
  report: TiendaPerfectaRouteReport | null;
  /** Cuándo se recibió la data que se está viendo. */
  lastUpdatedAt: Date | null;
  /** Una carga que el usuario estaba esperando falló y no dejó nada a la vista. */
  error: boolean;
  refresh: () => void;
}

/**
 * Cómo se comporta una carga. Son TRES decisiones independientes, y mezclarlas
 * fue justo el error anterior: avisar de un fallo no implica tirar lo que el
 * usuario está mirando.
 */
interface FetchOptions {
  /** Tapa todo con el skeleton. Solo cuando no hay nada que conservar. */
  skeleton: boolean;
  /** Un fallo se le dice al usuario. Lo que él pidió tiene que contestar. */
  announceErrors: boolean;
  /**
   * Un fallo descarta lo que hay en pantalla y muestra el estado de error.
   *
   * Solo la carga inicial: ahí no hay nada válido que conservar. Si el usuario
   * está viendo un día completo y aprieta actualizar, un 500 pasajero no puede
   * borrarle el reporte — se le avisa y se queda con lo que ya tenía, que
   * sigue siendo cierto.
   */
  discardOnError: boolean;
}

/**
 * El día completo, refrescado solo mientras se lo está mirando.
 *
 * Las cargas se distinguen por dos ejes que no van juntos:
 *
 * - **Skeleton** (`loading`): solo la primera carga de un recorte, cuando la
 *   pantalla todavía no tiene nada que mostrar. Un refresco que reemplace los
 *   datos por un skeleton haría parpadear todo y perdería la fila que el
 *   usuario tenía abierta.
 * - **Aviso de error**: lo que el usuario PIDIÓ —la carga inicial y el botón de
 *   actualizar— tiene que contestar aunque no lleve skeleton. Solo el reloj
 *   falla en silencio: un toast cada 30 segundos sería ruido.
 */
export function useRouteReport({
  date,
  distributorId,
}: UseRouteReportParams): UseRouteReportReturn {
  const { message } = App.useApp();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [report, setReport] = useState<TiendaPerfectaRouteReport | null>(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date | null>(null);
  const [error, setError] = useState(false);

  const timer = useRef<ReturnType<typeof setTimeout>>();
  /**
   * Número de la última petición disparada; gana siempre la más nueva.
   *
   * Antes se descartaba la petición nueva mientras hubiera una en curso, así que
   * cambiar de filtro durante una respuesta lenta no hacía nada: la pantalla se
   * quedaba con el recorte anterior y sin forma de enterarse. Ahora se dispara
   * igual y la respuesta vieja, que ya no corresponde a lo que el usuario tiene
   * seleccionado, llega y no escribe nada.
   */
  const requestId = useRef(0);
  /**
   * El efecto ya se limpió. Se consulta después de cada `await`: para entonces
   * la pantalla puede haberse desmontado, y ni el estado ni el próximo tick del
   * reloj tienen a dónde ir.
   */
  const active = useRef(true);
  /**
   * El recorte que ya se mostró: fecha MÁS distribuidor. Si cambia cualquiera de
   * los dos, lo que hay en pantalla dejó de corresponder y la carga vuelve a ser
   * "primera" — o sea, con skeleton.
   */
  const queryKey = `${date ?? ''}|${distributorId ?? 'all'}`;
  const loadedKey = useRef<string | undefined>(undefined);

  const fetchReport = useCallback(
    async ({ skeleton, announceErrors, discardOnError }: FetchOptions) => {
      if (!date) return;

      requestId.current += 1;
      const id = requestId.current;

      if (skeleton) setLoading(true);
      else setRefreshing(true);

      const response = await TiendaPerfectaService.getRouteReport({
        date,
        // Se omite en "Todos": mandar un parámetro vacío no es lo mismo que no
        // mandarlo.
        ...(distributorId ? { distributorId } : {}),
      });

      // Respuesta superada o pantalla desmontada: no escribe NADA, ni siquiera
      // el fin de la carga. Pisar el resultado vigente con uno viejo mostraría
      // un día que el usuario ya dejó atrás.
      if (id !== requestId.current || !active.current) return;

      const payload = response.data as TiendaPerfectaRouteReport;
      // Un `200` sin la forma del contrato es una respuesta rota, no un día sin
      // plan: sin esta comprobación se leería como un día vacío y la pantalla
      // diría que no hay rutas planificadas cuando en realidad no se sabe.
      const loaded = response.success && Array.isArray(payload?.routes);

      if (loaded) {
        setReport(payload);
        setLastUpdatedAt(new Date());
        setError(false);
        loadedKey.current = queryKey;
      } else {
        if (announceErrors) {
          const failure = response.data as ApiErrorData;
          message.error(
            (failure?.message as string) ||
              t('scenes.route_report.scene.errors.fetch_report'),
            3.5
          );
        }
        // Descartar es una decisión aparte de avisar: solo se vacía la pantalla
        // cuando lo que había tampoco servía.
        if (discardOnError) {
          setReport(null);
          setError(true);
        }
      }
      // El reloj que falla no interrumpe: se conserva lo que ya está en pantalla
      // y `lastUpdatedAt` deja de avanzar, que es la señal honesta de que lo que
      // se ve quedó viejo.

      setRefreshing(false);
      setLoading(false);
    },
    [date, distributorId, queryKey, message, t]
  );

  /**
   * Reprograma desde que la petición TERMINA, no desde que arrancó: con una
   * respuesta lenta, un `setInterval` encolaría llamadas en vez de espaciarlas.
   */
  const scheduleNext = useCallback(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      if (!active.current) return;

      // Con la pestaña de fondo no tiene sentido consultar: nadie lo está
      // mirando y el navegador además estrangula los timers. Se retoma al
      // volver, con un refresco inmediato.
      if (document.visibilityState === 'visible') {
        await fetchReport({
          skeleton: false,
          announceErrors: false,
          discardOnError: false,
        });
      }

      // El re-armado pasa DESPUÉS de esperar la respuesta, así que la limpieza
      // pudo correr mientras tanto: sin este chequeo el reloj sobreviviría a la
      // pantalla y seguiría pidiendo el día para siempre.
      if (active.current) scheduleNext();
    }, POLL_INTERVAL_MS);
  }, [fetchReport]);

  // El botón lo apretó el usuario: no lleva skeleton porque ya hay datos, pero
  // un fallo mudo dejaría el gesto sin respuesta.
  const refresh = useCallback(() => {
    fetchReport({
      skeleton: false,
      announceErrors: true,
      discardOnError: false,
    });
    scheduleNext();
  }, [fetchReport, scheduleNext]);

  useEffect(() => {
    active.current = true;

    if (date) {
      // Cambiar de fecha o de distribuidor es empezar de nuevo: ahí sí
      // corresponde el skeleton.
      // Al entrar o al cambiar de recorte se limpia el error anterior: si no,
      // la tarjeta de fallo tapaba el skeleton de la consulta nueva.
      setError(false);
      fetchReport({
        skeleton: loadedKey.current !== queryKey,
        announceErrors: true,
        discardOnError: true,
      });
      scheduleNext();
    } else {
      setReport(null);
      setError(false);
    }

    // La limpieza se devuelve SIEMPRE, también sin fecha: el timer pudo quedar
    // armado por un render anterior que sí la tenía.
    return () => {
      active.current = false;
      clearTimeout(timer.current);
    };
  }, [date, queryKey, fetchReport, scheduleNext]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      // Al volver, lo que hay en pantalla puede tener minutos de atraso. Sigue
      // siendo automático, así que tampoco avisa si falla.
      fetchReport({
        skeleton: false,
        announceErrors: false,
        discardOnError: false,
      });
      scheduleNext();
    };

    document.addEventListener('visibilitychange', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      clearTimeout(timer.current);
    };
  }, [fetchReport, scheduleNext]);

  return { loading, refreshing, report, lastUpdatedAt, error, refresh };
}
